/**
 * OpuID - Compact auction identifier system
 * @package thael.opuid
 * @version 1.0.0
 * @author thael
 * 
 * Encodes seller UUID, auction UUID (derived from startTime), creation timestamp,
 * and expiry into a compact, URL-safe, QR-code friendly string.
 */

// 32-character alphabet (2^5 = 32 perfect for 5-bit groups)
// Only QR-Code Alphanumeric Mode characters
// No confusable characters: O, I, 0, 1
const ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

// Reverse lookup for decoding
const REVERSE_ALPHABET: Record<string, number> = {};
for (let i = 0; i < ALPHABET.length; i++) {
  REVERSE_ALPHABET[ALPHABET[i]] = i;
}

/**
 * CRC-16-CCITT checksum calculation
 */
function crc16(hexString: string): number {
  let crc = 0xFFFF;
  
  for (let i = 0; i < hexString.length; i += 2) {
    const byte = parseInt(hexString.slice(i, i + 2), 16);
    crc ^= (byte << 8);
    
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = ((crc << 1) ^ 0x1021) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }
  
  return crc & 0xFFFF;
}

/**
 * Convert hex string to UUID format
 */
function hexToUUID(hex: string): string {
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32)
  ].join('-');
}

/**
 * Generate a deterministic UUID from a string (for auction startTime -> UUID)
 */
function stringToUUID(str: string): string {
  // Simple hash-based UUID generation
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  // Create UUID-like hex string from hash + timestamp string
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  const timeHex = str.split('').reduce((acc, c, i) => {
    return acc + c.charCodeAt(0).toString(16).padStart(2, '0').slice(-2);
  }, '').slice(0, 24);
  
  const fullHex = (hex + timeHex).padEnd(32, '0').slice(0, 32);
  
  return hexToUUID(fullHex);
}

export interface OpuIDData {
  userUUID: string;
  auctionUUID: string;
  createdAt: Date;
  expiresAt: Date;
  expiresInMinutes: number;
  isExpired: boolean;
  valid: boolean;
  error?: string;
  crc?: {
    stored: string;
    calculated: string;
  };
}

/**
 * Encode auction data into a compact OpuID
 */
export function encodeOpuID(
  sellerUUID: string,
  startTime: string | Date,
  expiresInMinutes: number = 10080 // Default 7 days
): string {
  // 1. UUIDs to hex strings (32 chars each)
  const userHex = sellerUUID.replace(/-/g, '');
  
  // Generate auction UUID from startTime
  const startTimeStr = startTime instanceof Date ? startTime.toISOString() : startTime;
  const auctionUUID = stringToUUID(startTimeStr);
  const auctionHex = auctionUUID.replace(/-/g, '');
  
  // 2. Timestamp (64 bit = 16 hex chars)
  const createdAt = startTime instanceof Date ? startTime : new Date(startTime);
  const timestamp = createdAt.getTime();
  const timestampHex = timestamp.toString(16).padStart(16, '0');
  
  // 3. Expiry time (32 bit = 8 hex chars)
  const expiryHex = expiresInMinutes.toString(16).padStart(8, '0');
  
  // 4. Combine all data
  const dataHex = userHex + auctionHex + timestampHex + expiryHex;
  // Total: 32 + 32 + 16 + 8 = 88 hex chars = 352 bits
  
  // 5. Calculate CRC-16 checksum
  const crc = crc16(dataHex);
  const crcHex = crc.toString(16).padStart(4, '0');
  
  // 6. Final hex string (88 + 4 = 92 chars)
  const finalHex = dataHex + crcHex;
  
  // 7. Convert hex to custom alphabet (5 hex = 20 bits = 4 custom chars)
  let result = '';
  for (let i = 0; i < finalHex.length; i += 5) {
    const chunk = finalHex.slice(i, i + 5).padEnd(5, '0');
    const value = parseInt(chunk, 16);
    
    // 20 bits to 4x 5-bit characters
    for (let j = 3; j >= 0; j--) {
      const index = (value >> (j * 5)) & 0x1F;
      result += ALPHABET[index];
    }
  }
  
  return result;
}

/**
 * Decode an OpuID back to its components
 */
export function decodeOpuID(compactId: string): OpuIDData {
  try {
    // 1. Custom alphabet to hex
    let hex = '';
    for (let i = 0; i < compactId.length; i += 4) {
      const chunk = compactId.slice(i, i + 4);
      let value = 0;
      
      for (let j = 0; j < 4; j++) {
        const char = chunk[j] || ALPHABET[0];
        const index = REVERSE_ALPHABET[char];
        if (index === undefined) {
          throw new Error(`Invalid character: ${char}`);
        }
        value = (value << 5) | index;
      }
      
      // 20 bits to 5 hex chars
      hex += value.toString(16).padStart(5, '0');
    }
    
    // 2. Split hex string into components
    const userHex = hex.slice(0, 32);
    const auctionHex = hex.slice(32, 64);
    const timestampHex = hex.slice(64, 80);
    const expiryHex = hex.slice(80, 88);
    const storedCrcHex = hex.slice(88, 92);
    
    // 3. Validate CRC
    const dataHex = hex.slice(0, 88);
    const calculatedCrc = crc16(dataHex);
    const storedCrc = parseInt(storedCrcHex, 16);
    const valid = calculatedCrc === storedCrc;
    
    // 4. Reconstruct UUIDs
    const userUUID = hexToUUID(userHex);
    const auctionUUID = hexToUUID(auctionHex);
    
    // 5. Reconstruct timestamp
    const timestamp = parseInt(timestampHex, 16);
    const createdAt = new Date(timestamp);
    
    // 6. Reconstruct expiry
    const expiresInMinutes = parseInt(expiryHex, 16);
    const expiresAt = new Date(createdAt.getTime() + expiresInMinutes * 60000);
    
    // 7. Check status
    const isExpired = Date.now() > expiresAt.getTime();
    
    return {
      userUUID,
      auctionUUID,
      createdAt,
      expiresAt,
      expiresInMinutes,
      isExpired,
      valid,
      crc: {
        stored: storedCrcHex.toUpperCase(),
        calculated: calculatedCrc.toString(16).padStart(4, '0').toUpperCase()
      }
    };
  } catch (error) {
    return {
      userUUID: '',
      auctionUUID: '',
      createdAt: new Date(0),
      expiresAt: new Date(0),
      expiresInMinutes: 0,
      isExpired: true,
      valid: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Check if an OpuID is valid (correct format and not expired)
 */
export function isValidOpuID(compactId: string): boolean {
  const decoded = decodeOpuID(compactId);
  return decoded.valid && !decoded.isExpired;
}

/**
 * Generate OpuID from auction data (convenience function)
 */
export function generateAuctionOpuID(
  sellerUUID: string,
  startTime: string,
  endTime: string
): string {
  // Calculate expiry based on end time
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  const expiresInMinutes = Math.ceil((endDate.getTime() - startDate.getTime()) / 60000) + 10080; // Add 7 days buffer
  
  return encodeOpuID(sellerUUID, startTime, expiresInMinutes);
}

/**
 * Get seller UUID from OpuID (for quick lookup)
 */
export function getSellerFromOpuID(compactId: string): string | null {
  const decoded = decodeOpuID(compactId);
  return decoded.valid ? decoded.userUUID : null;
}

/**
 * Legacy support: Convert old base64 auction ID to new OpuID format
 */
export function convertLegacyAuctionId(base64Id: string): { opuid: string; sellerUUID: string; startTime: string } | null {
  try {
    const decoded = atob(base64Id);
    const [sellerUUID, startTime] = decoded.split('|');
    
    if (!sellerUUID || !startTime) return null;
    
    const opuid = generateAuctionOpuID(sellerUUID, startTime, new Date(new Date(startTime).getTime() + 24 * 60 * 60 * 1000).toISOString());
    
    return { opuid, sellerUUID, startTime };
  } catch {
    return null;
  }
}

/**
 * Generate auction key from OpuID (for database lookup)
 */
export function getAuctionKeyFromOpuID(compactId: string): string | null {
  const decoded = decodeOpuID(compactId);
  if (!decoded.valid) return null;
  
  // Return seller|startTime format for backwards compatibility
  return `${decoded.userUUID}|${decoded.createdAt.toISOString()}`;
}

/**
 * Convert a raw auction_key ("sellerUUID|startTimeISO") to an OpuID for URL navigation.
 * If endTime is available, it's used for accurate expiry calculation.
 * Falls back to a generous default expiry if endTime is not provided.
 */
export function auctionKeyToOpuID(auctionKey: string, endTime?: string): string | null {
  try {
    const pipeIndex = auctionKey.indexOf('|');
    if (pipeIndex === -1) return null;
    
    const sellerUUID = auctionKey.substring(0, pipeIndex);
    const startTime = auctionKey.substring(pipeIndex + 1);
    
    if (!sellerUUID || !startTime) return null;
    
    const effectiveEndTime = endTime || new Date(new Date(startTime).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
    return generateAuctionOpuID(sellerUUID, startTime, effectiveEndTime);
  } catch {
    return null;
  }
}

/**
 * Check if a string looks like an OpuID (uses custom alphabet, no pipe character)
 */
export function isOpuIDFormat(str: string): boolean {
  return str.length > 20 && !str.includes('|') && !str.includes('-');
}
